import type {
  CertificationStatus,
  EnrichmentLevel,
  FrictionKind,
  OutcomeSource,
  PainPointKind,
  SessionOutcome,
  SignalDefinitionStatus,
  SignalTierClass,
} from "@ti/core";
import { SESSION_OUTCOMES } from "@ti/core";
import {
  BadgeCheck,
  Bot,
  Briefcase,
  CircleDashed,
  CirclePause,
  CirclePlay,
  CircleSlash,
  Eye,
  FlaskConical,
  Radio,
  Sparkles,
  Zap,
} from "lucide-react";
import type { ReactNode } from "react";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Tooltip } from "@/components/ui/tooltip";
import { fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";

/* Shared building blocks for the analytics screens (docs 14, 15). */

// ─── Outcomes ────────────────────────────────────────────────────────────────

export const OUTCOME_LABEL: Record<SessionOutcome, string> = {
  resolved: "Resolved",
  converted: "Converted",
  partially_resolved: "Partly resolved",
  escalated: "Escalated",
  abandoned: "Abandoned",
  failed: "Failed",
  unknown: "Unknown",
};

export const OUTCOME_TONE: Record<SessionOutcome, BadgeTone> = {
  resolved: "good",
  converted: "good",
  partially_resolved: "warning",
  escalated: "info",
  abandoned: "neutral",
  failed: "critical",
  unknown: "muted",
};

/** Chart colors per outcome, in the order of SESSION_OUTCOMES. */
export const OUTCOME_COLOR: Record<SessionOutcome, string> = {
  resolved: "var(--good)",
  converted: "var(--series-1)",
  partially_resolved: "var(--warning)",
  escalated: "var(--violet)",
  abandoned: "var(--neutral-status)",
  failed: "var(--critical)",
  unknown: "var(--border-strong)",
};

const SOURCE: Record<OutcomeSource, { icon: ReactNode; label: string; help: string }> = {
  event: { icon: <Zap />, label: "Event", help: "Sent by your systems with track() or outcome()" },
  business: { icon: <Briefcase />, label: "Business", help: "Joined from a connected business system" },
  inferred: { icon: <Bot />, label: "Inferred", help: "Inferred from the transcript by the analytics model" },
};

export function OutcomeSourceIcon({ source, withLabel }: { source: OutcomeSource; withLabel?: boolean }) {
  const s = SOURCE[source];
  return (
    <Tooltip content={s.help}>
      <span className="inline-flex items-center gap-1 text-[11px] text-fg-3 [&_svg]:size-3">
        {s.icon}
        {withLabel ? s.label : <span className="sr-only">{s.label}</span>}
      </span>
    </Tooltip>
  );
}

export function OutcomeChip({ outcome, source, className }: { outcome: SessionOutcome; source?: OutcomeSource; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <Badge tone={OUTCOME_TONE[outcome]}>{OUTCOME_LABEL[outcome]}</Badge>
      {source ? <OutcomeSourceIcon source={source} /> : null}
    </span>
  );
}

/** Horizontal stacked bar of an outcome mix. */
export function OutcomeMixBar({ mix, className, height = 8, showLegend }: { mix: Partial<Record<SessionOutcome, number>>; className?: string; height?: number; showLegend?: boolean }) {
  const total = SESSION_OUTCOMES.reduce((s, o) => s + (mix[o] ?? 0), 0);
  if (!total) return <span className="text-xs text-fg-3">—</span>;
  const parts = SESSION_OUTCOMES.filter((o) => (mix[o] ?? 0) > 0);
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="flex w-full overflow-hidden rounded-sm" style={{ height }} role="img" aria-label={parts.map((o) => `${OUTCOME_LABEL[o]} ${fmtPct((mix[o] ?? 0) / total)}`).join(", ")}>
        {parts.map((o) => (
          <Tooltip key={o} content={`${OUTCOME_LABEL[o]}: ${fmtPct((mix[o] ?? 0) / total)} (${mix[o]})`}>
            <span className="h-full" style={{ width: `${((mix[o] ?? 0) / total) * 100}%`, background: OUTCOME_COLOR[o] }} />
          </Tooltip>
        ))}
      </div>
      {showLegend ? (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-fg-3">
          {parts.map((o) => (
            <span key={o} className="inline-flex items-center gap-1">
              <span className="size-2 rounded-sm" style={{ background: OUTCOME_COLOR[o] }} />
              {OUTCOME_LABEL[o]} <span className="tabular text-fg-2">{fmtPct((mix[o] ?? 0) / total)}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Success rate with a color that reads at a glance (always paired with the number). */
export function SuccessRate({ value, className }: { value: number; className?: string }) {
  const color = value >= 0.8 ? "text-good-text" : value >= 0.6 ? "text-warning-text" : "text-critical-text";
  return <span className={cn("font-medium tabular", color, className)}>{fmtPct(value)}</span>;
}

// ─── Friction & sentiment ────────────────────────────────────────────────────

export const FRICTION_LABEL: Record<FrictionKind, string> = {
  rephrase: "Rephrased",
  repeat_question: "Asked twice",
  human_request: "Asked for a human",
  negative_sentiment: "Negative turn",
  agent_loop: "Agent loop",
  dead_end: "Dead end",
  unanswered: "Unanswered",
  tool_failure: "Tool failure",
  slow_response: "Slow response",
  knowledge_gap: "Knowledge gap",
  unsupported_request: "Unsupported request",
  policy_refusal: "Policy refusal",
};

const FRICTION_TONE: Partial<Record<FrictionKind, BadgeTone>> = {
  human_request: "serious",
  agent_loop: "serious",
  tool_failure: "serious",
  dead_end: "warning",
  negative_sentiment: "warning",
  knowledge_gap: "violet",
  unsupported_request: "violet",
};

export function FrictionChip({ kind, count }: { kind: FrictionKind; count?: number }) {
  return (
    <Badge tone={FRICTION_TONE[kind] ?? "neutral"}>
      {FRICTION_LABEL[kind]}
      {count !== undefined ? <span className="tabular opacity-70">{count}</span> : null}
    </Badge>
  );
}

const sentimentColor = (v: number) => (v >= 0.25 ? "var(--good)" : v <= -0.25 ? "var(--critical)" : "var(--neutral-status)");
const sentimentWord = (v: number) => (v >= 0.25 ? "positive" : v <= -0.25 ? "negative" : "neutral");

/** Sentiment start → end, as two dots and an arrow; tooltip carries the numbers. */
export function SentimentDelta({ start, end }: { start: number; end: number }) {
  return (
    <Tooltip content={`Sentiment ${start.toFixed(2)} → ${end.toFixed(2)}`}>
      <span className="inline-flex items-center gap-1 text-[11px] text-fg-3" aria-label={`Sentiment from ${sentimentWord(start)} to ${sentimentWord(end)}`}>
        <span className="size-2 rounded-full" style={{ background: sentimentColor(start) }} />→
        <span className="size-2 rounded-full" style={{ background: sentimentColor(end) }} />
      </span>
    </Tooltip>
  );
}

// ─── Enrichment ──────────────────────────────────────────────────────────────

const ENRICHMENT: Record<EnrichmentLevel, { tone: BadgeTone; label: string; help: string }> = {
  full: { tone: "accent", label: "Enriched", help: "Analyzed from PII-redacted content" },
  metadata_only: { tone: "neutral", label: "Metadata only", help: "Privacy mode keeps content with you; structural facts only" },
  sampled_out: { tone: "muted", label: "Sampled", help: "Embeddings and rules only; the model pass was skipped by sampling" },
  pending: { tone: "muted", label: "Pending", help: "Session still open or queued for enrichment" },
};

export function EnrichmentBadge({ level }: { level: EnrichmentLevel }) {
  const e = ENRICHMENT[level];
  return (
    <Tooltip content={e.help}>
      <Badge tone={e.tone}>
        <Sparkles /> {e.label}
      </Badge>
    </Tooltip>
  );
}

// ─── Signal definitions ──────────────────────────────────────────────────────

const DEF_STATUS: Record<SignalDefinitionStatus, { tone: BadgeTone; icon: ReactNode; label: string }> = {
  planned: { tone: "muted", icon: <CircleDashed />, label: "Planned" },
  proposed: { tone: "accent", icon: <Sparkles />, label: "Proposed" },
  backtesting: { tone: "violet", icon: <FlaskConical />, label: "Backtesting" },
  shadow: { tone: "info", icon: <Eye />, label: "Shadow" },
  active: { tone: "good", icon: <CirclePlay />, label: "Active" },
  paused: { tone: "warning", icon: <CirclePause />, label: "Paused" },
  retired: { tone: "muted", icon: <CircleSlash />, label: "Retired" },
  dismissed: { tone: "muted", icon: <CircleDashed />, label: "Dismissed" },
};

export function SignalDefinitionStatusBadge({ status }: { status: SignalDefinitionStatus }) {
  const s = DEF_STATUS[status];
  return (
    <Badge tone={s.tone}>
      {s.icon}
      {s.label}
    </Badge>
  );
}

export function SignalTierBadge({ tier, certification }: { tier: SignalTierClass; certification?: CertificationStatus }) {
  if (tier === "risk")
    return (
      <Tooltip content="Curated and calibrated; feeds the reliability score and pricing">
        <Badge tone="serious">
          <BadgeCheck /> Risk
        </Badge>
      </Tooltip>
    );
  return (
    <Tooltip content={certification && certification !== "none" ? `Analytics signal · certification ${certification.replace("_", " ")}` : "Analytics signal: dashboards, alerts and issues; never moves price"}>
      <Badge tone="info">
        <Radio /> Analytics{certification === "nominated" || certification === "under_review" ? " · nominated" : ""}
      </Badge>
    </Tooltip>
  );
}

/** Measured precision with its label count; "—" until enough reviews. */
export function PrecisionBadge({ value, labeled }: { value: number | null; labeled: number }) {
  if (value === null)
    return (
      <Tooltip content={`${labeled} reviewed hits; precision appears after 20`}>
        <span className="text-xs text-fg-3">— ({labeled})</span>
      </Tooltip>
    );
  const tone: BadgeTone = value >= 0.9 ? "good" : value >= 0.8 ? "info" : "warning";
  return (
    <Tooltip content={`Precision from ${labeled} reviewed hits`}>
      <Badge tone={tone} className="tabular">
        {fmtPct(value)} <span className="opacity-70">n={labeled}</span>
      </Badge>
    </Tooltip>
  );
}

// ─── Pain points ─────────────────────────────────────────────────────────────

export const PAIN_KIND: Record<PainPointKind, { label: string; tone: BadgeTone }> = {
  friction: { label: "Friction", tone: "warning" },
  knowledge_gap: { label: "Knowledge gap", tone: "violet" },
  unmet_demand: { label: "Unmet demand", tone: "accent" },
  failure_mode: { label: "Failure mode", tone: "serious" },
};

export function PainKindBadge({ kind }: { kind: PainPointKind }) {
  return <Badge tone={PAIN_KIND[kind].tone}>{PAIN_KIND[kind].label}</Badge>;
}

/** Signed percentage-point change (success impact etc.). */
export function PointsDelta({ value, className }: { value: number; className?: string }) {
  const pts = Math.round(value * 100);
  const color = pts > 0 ? "text-good-text" : pts < 0 ? "text-critical-text" : "text-fg-3";
  return <span className={cn("tabular text-xs font-medium", color, className)}>{pts > 0 ? "+" : ""}{pts} pts</span>;
}
